import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi, type SessionUser } from './api';
import { ApiError } from '@/lib/api';
import { clearRuntimeCaches } from './clearRuntimeCaches';

/** Stands in for /api/auth/me while the browser is offline (a cold boot from the service worker). */
const OFFLINE_USER: SessionUser = { id: 'offline', email: '', name: 'Offline session', role: 'user' };

export function useSession() {
  const query = useQuery<SessionUser, ApiError>({
    queryKey: ['session'],
    // Offline, the shell renders with the stand-in (the top bar shows the offline badge) instead
    // of the "cannot reach the server" splash; it re-checks until the network answers.
    queryFn: () => (navigator.onLine ? authApi.me() : Promise.resolve(OFFLINE_USER)),
    refetchInterval: (q) => (q.state.data === OFFLINE_USER ? 15_000 : false),
    retry: (count, err) => err.status !== 401 && count < 2,
    staleTime: 60_000,
  });
  return query;
}

export function useLogout() {
  const qc = useQueryClient();
  return async () => {
    await authApi.logout();
    // The service worker's runtime caches hold this session's API reads, live readings and map
    // tiles; the precache (shell and /data) stays so the app still boots offline. A failure is
    // logged and the sign-out goes on: the server session is already gone.
    try {
      await clearRuntimeCaches();
    } catch (err) {
      console.error('[auth] logout could not clear the service worker caches:', err);
    }
    qc.clear();
    window.location.assign('/login');
  };
}
